import { normalizeLoggedUsersFallback } from "./loggedUsersFallback";

/** Fases do ciclo de vida configuráveis por skill (espelha backend). */
export const LIFECYCLE_EVENTS = [
  "on_start",
  "on_success",
  "on_failure",
  "on_escalation",
  "on_outside_hours",
  "on_inactivity",
];

export const DEFAULT_IDLE_MINUTES = 5;
/** Mínimo de produto (worker a cada 1 min; não nague o cliente antes disso). */
export const MIN_IDLE_MINUTES = 5;
export const MAX_IDLE_MINUTES = 10080;

export const DETECTION_MODE_OPTIONS = [
  { value: "auto", labelKey: "agentsListPage.lifecycleDetectionAuto" },
  { value: "keywords", labelKey: "agentsListPage.lifecycleDetectionKeywords" },
  { value: "auto_or_keywords", labelKey: "agentsListPage.lifecycleDetectionBoth" },
];

export const TRANSFER_DEST_OPTIONS = [
  { value: "none", labelKey: "agentsListPage.lifecycleTransferNone" },
  { value: "fila_humano", labelKey: "agentsListPage.lifecycleTransferQueue" },
  { value: "humano", labelKey: "agentsListPage.lifecycleTransferHuman" },
  { value: "agente_ia", labelKey: "agentsListPage.lifecycleTransferAgent" },
  { value: "chatbot", labelKey: "agentsListPage.lifecycleTransferChatbot" },
  { value: "receptionist", labelKey: "agentsListPage.lifecycleTransferReceptionist" },
];

export const STATUS_OPTIONS = [
  { value: "", labelKey: "agentsListPage.lifecycleStatusKeep" },
  { value: 0, labelKey: "agentsListPage.lifecycleStatusPending" },
  { value: 1, labelKey: "agentsListPage.lifecycleStatusInProgress" },
  { value: 2, labelKey: "agentsListPage.lifecycleStatusCompleted" },
  { value: 3, labelKey: "agentsListPage.lifecycleStatusFuture" },
  { value: 4, labelKey: "agentsListPage.lifecycleStatusLost" },
  { value: 5, labelKey: "agentsListPage.lifecycleStatusCanceled" },
];

const DEFAULT_RULE = {
  enabled: false,
  detection_mode: "auto",
  trigger_keywords: "",
  status: null,
  transfer: {
    dest: "none",
    target_id: "",
    tipo_id: "",
    tipo1_id: "",
    tipo2_id: "",
    tipo3_id: "",
    logged_users_only: false,
    logged_users_fallback: null,
  },
  message: "",
  idle_minutes: DEFAULT_IDLE_MINUTES,
};

/** Chaves i18n por skill (agentsListPage.lifecycleBySkill.<skill>). */
export const LIFECYCLE_SKILL_KEYS = [
  "receptionist",
  "faq",
  "ticket_seller",
  "ticket_preseller",
  "proposal_generator",
  "contract_generator",
  "appointment_scheduler",
  "sdr",
  "social_media",
  "product_seller",
  "complaints_suggestions",
  "passagem_recovery",
  "ops_intelligence",
];

export function lifecycleTextKey(skillKey, suffix) {
  const sk = LIFECYCLE_SKILL_KEYS.includes(skillKey) ? skillKey : "generic";
  return `agentsListPage.lifecycleBySkill.${sk}.${suffix}`;
}

export function skillFormTextKey(skillKey, suffix) {
  const sk = LIFECYCLE_SKILL_KEYS.includes(skillKey) ? skillKey : "generic";
  return `agentsListPage.skillFormCopy.${sk}.${suffix}`;
}

/** Texto por skill com fallback para generic e depois chave global. */
export function translateSkillCopy(t, skillKey, namespace, suffix, fallbackKey) {
  const sk = LIFECYCLE_SKILL_KEYS.includes(skillKey) ? skillKey : "generic";
  const key = `agentsListPage.${namespace}.${sk}.${suffix}`;
  const value = t(key);
  if (value !== key) return value;
  if (sk !== "generic") {
    const genericKey = `agentsListPage.${namespace}.generic.${suffix}`;
    const genericVal = t(genericKey);
    if (genericVal !== genericKey) return genericVal;
  }
  return fallbackKey ? t(fallbackKey) : value;
}

export function translateLifecycle(t, skillKey, suffix, fallbackKey) {
  return translateSkillCopy(t, skillKey, "lifecycleBySkill", suffix, fallbackKey);
}

export function translateSkillForm(t, skillKey, suffix, fallbackKey) {
  return translateSkillCopy(t, skillKey, "skillFormCopy", suffix, fallbackKey);
}

export function normalizeIdleMinutes(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_IDLE_MINUTES;
  return Math.min(MAX_IDLE_MINUTES, Math.max(MIN_IDLE_MINUTES, Math.round(n)));
}

function isLifecycleRuleEnabled(raw) {
  return raw === true || raw === 1 || raw === "1" || String(raw || "").toLowerCase() === "true";
}

/** Fases omitidas quando não há copy específica (ex.: fora do horário só no FAQ). */
export function lifecycleEventsForSkill(skillKey) {
  if (skillKey === "faq") return [...LIFECYCLE_EVENTS];
  return LIFECYCLE_EVENTS.filter((ev) => ev !== "on_outside_hours");
}

export function isInactivityLifecycleEvent(event) {
  return event === "on_inactivity";
}

export function normalizeLifecycleRule(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_RULE, transfer: { ...DEFAULT_RULE.transfer } };
  }
  const statusRaw = raw.status;
  const status =
    statusRaw === null || statusRaw === undefined || statusRaw === "" ? null : Number(statusRaw);
  const transfer = raw.transfer && typeof raw.transfer === "object" ? raw.transfer : {};
  const trigger_keywords =
    typeof raw.trigger_keywords === "string"
      ? raw.trigger_keywords
      : Array.isArray(raw.trigger_keywords)
        ? raw.trigger_keywords.join(", ")
        : "";
  const mode = raw.detection_mode || (trigger_keywords.trim() ? "auto_or_keywords" : "auto");
  const loggedUsersOnly =
    String(transfer.dest || "none").toLowerCase() === "humano" &&
    (transfer.logged_users_only === true ||
      transfer.logged_users_only === 1 ||
      transfer.logged_users_only === "1" ||
      transfer.somenteusuarioslogados === true ||
      transfer.somenteusuarioslogados === 1 ||
      String(transfer.logged_users_only || "")
        .trim()
        .toLowerCase() === "true" ||
      String(transfer.somenteusuarioslogados || "")
        .trim()
        .toLowerCase() === "sim");
  return {
    enabled: isLifecycleRuleEnabled(raw.enabled),
    detection_mode: ["auto", "keywords", "auto_or_keywords"].includes(mode) ? mode : "auto",
    trigger_keywords,
    status: Number.isFinite(status) ? status : null,
    transfer: {
      dest: transfer.dest || "none",
      target_id: transfer.target_id != null ? String(transfer.target_id) : "",
      tipo_id: transfer.tipo_id != null ? String(transfer.tipo_id) : "",
      tipo1_id: transfer.tipo1_id != null ? String(transfer.tipo1_id) : "",
      tipo2_id: transfer.tipo2_id != null ? String(transfer.tipo2_id) : "",
      tipo3_id: transfer.tipo3_id != null ? String(transfer.tipo3_id) : "",
      logged_users_only: loggedUsersOnly,
      logged_users_fallback: loggedUsersOnly
        ? normalizeLoggedUsersFallback(transfer.logged_users_fallback)
        : null,
    },
    message: typeof raw.message === "string" ? raw.message : "",
    idle_minutes: normalizeIdleMinutes(raw.idle_minutes ?? raw.minutos_inatividade),
  };
}

export function getLifecycleFromConfig(skillConfig) {
  const lc = skillConfig?.lifecycle;
  const out = {};
  for (const ev of LIFECYCLE_EVENTS) {
    out[ev] = normalizeLifecycleRule(lc?.[ev]);
  }
  return out;
}

function isTruthyLoggedUsersOnly(raw) {
  return (
    raw === true ||
    raw === 1 ||
    raw === "1" ||
    String(raw || "")
      .trim()
      .toLowerCase() === "true" ||
    String(raw || "")
      .trim()
      .toLowerCase() === "sim"
  );
}

/**
 * Mescla patch de evento sobre o lifecycle já normalizado.
 * `logged_users_only` só muda quando o patch traz a chave explicitamente (checkbox);
 * seleção de destinatários não pode apagar o flag por closure stale.
 */
export function mergeLifecycleEvent(lifecycle, event, partial) {
  const current = normalizeLifecycleRule(lifecycle?.[event]);
  const next = { ...current, ...partial };
  if (partial?.transfer && typeof partial.transfer === "object") {
    const incoming = partial.transfer;
    const dest = String(incoming.dest || current.transfer.dest || "none").toLowerCase();
    const hasLoggedFlag = Object.prototype.hasOwnProperty.call(incoming, "logged_users_only");
    const hasFallback = Object.prototype.hasOwnProperty.call(incoming, "logged_users_fallback");
    next.transfer = normalizeLifecycleRule({
      ...current,
      transfer: {
        ...current.transfer,
        ...incoming,
        logged_users_only:
          dest === "humano" &&
          (hasLoggedFlag ? isTruthyLoggedUsersOnly(incoming.logged_users_only) : current.transfer.logged_users_only === true),
        logged_users_fallback: hasFallback
          ? incoming.logged_users_fallback
          : current.transfer.logged_users_fallback,
      },
    }).transfer;
  }
  if (partial?.idle_minutes != null) {
    next.idle_minutes = normalizeIdleMinutes(partial.idle_minutes);
  }
  return { ...lifecycle, [event]: normalizeLifecycleRule(next) };
}

/** Destinos que aceitam tipo de atendimento no lead. */
export function transferNeedsLeadType(dest) {
  const d = String(dest || "").trim().toLowerCase();
  return d && d !== "none";
}
